import React, { useState, useEffect, useRef } from 'react';
import JobListingCard from './JobListingCard';
import JobListingCardSkeleton from './JobListingCardSkeleton';

interface JobListing {
  id: number;
  title: string;
  max_reward: number | null;
  min_reward: number | null;
  contract_type: string;
  prefecture: { id: number; name: string } | null;
  city: string | null;
  station: string | null;
  workstyle_type: number;
  workstyle_type_label: string | null;
  skills: Array<{ id: number; name: string }>;
  occupations: Array<{ id: number; name: string }>;
  characteristics: Array<{ id: number; name: string }>;
  industries?: Array<{ id: number; name: string }>;
  availability_type?: number;
  status?: string;
  job_source: { id: number; name: string; display_name?: string };
  published_at: string;
  created_at: string;
  is_new: boolean;
  is_favorited: boolean;
}

interface JobListingListProps {
  jobListings: JobListing[];
  selectedJobId?: number;
  onJobSelect: (jobId: number) => void;
  totalCount: number;
  from: number;
  to: number;
  currentPage: number;
  perPage: number;
  onPageChange: (page: number) => void;
  sortBy: string;
  onSortChange: (sort: string) => void;
  searchFilters?: {
    keyword?: string;
    prefecture_ids?: number[];
    price_over?: number;
    is_recruiting?: boolean;
    remote_types?: string[];
    skill_ids?: number[];
    occupation_ids?: number[];
    industry_ids?: number[];
    workday_types?: string[];
    characteristic_ids?: number[];
    workstyle_types?: string[];
    availability_types?: string[];
    payment_site?: string;
    settlement_hours?: string;
    interview_count_max?: number;
    published_within?: number;
    job_source_ids?: number[];
  };
  onRemoveFilter?: (filterKey: string, value?: any) => void;
  skillNames?: {[key: number]: string};
  occupationNames?: {[key: number]: string};
  industryNames?: {[key: number]: string};
  characteristicNames?: {[key: number]: string};
  prefectureNames?: {[key: number]: string};
  jobSourceNames?: {[key: number]: string};
  isLoading?: boolean;
}

const JobListingList: React.FC<JobListingListProps> = ({
  jobListings,
  selectedJobId,
  onJobSelect,
  totalCount,
  from,
  to,
  currentPage,
  perPage,
  onPageChange,
  sortBy,
  onSortChange,
  searchFilters,
  onRemoveFilter,
  skillNames = {},
  occupationNames = {},
  industryNames = {},
  characteristicNames = {},
  prefectureNames = {},
  jobSourceNames = {},
  isLoading = false
}) => {
  const sidebarRef = useRef<HTMLDivElement>(null);

  /**
   * Handle page change with scroll to top functionality for PC view
   * @param page - The page number to navigate to
   */
  const handlePageChangeWithScroll = (page: number) => {
    // Call the original onPageChange function
    onPageChange(page);
    
    // Scroll to top on PC view (768px and above)
    if (window.innerWidth >= 768) {
      // ページの更新が完了するまで少し待ってからスクロール処理を実行
      setTimeout(() => {
        // ERBテンプレートの要素を確実に発見するためのヘルパー関数
        const findERBElement = (selector: string, maxRetries = 10, delay = 50) => {
          return new Promise<Element | null>((resolve) => {
            let retries = 0;
            const checkElement = () => {
              const element = document.querySelector(selector);
              if (element) {
                resolve(element);
              } else if (retries < maxRetries) {
                retries++;
                setTimeout(checkElement, delay);
              } else {
                resolve(null);
              }
            };
            checkElement();
          });
        };

        // 1. 左サイドバーのスクロール
        findERBElement('[data-job-listings="true"]').then((scrollableParent) => {
          if (scrollableParent) {
            scrollableParent.scrollTo({ top: 0, behavior: 'smooth' });
          }
        });
        
        // 2. 右カラム（求人詳細）のスクロール
        findERBElement('.col-md-8.col-lg-8.d-none.d-md-block').then((rightColumn) => {
          if (rightColumn) {
            rightColumn.scrollTo({ top: 0, behavior: 'smooth' });
          }
        });
        
        // 3. ページ全体のスクロール - より確実な方法
        window.scrollTo({ top: 0, behavior: 'smooth' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 200); // 遅延を少し長くして確実性を向上
    }
  };

  /**
   * Handle job selection with scroll to top functionality for right panel
   * @param jobId - The job ID to select
   */
  const handleJobSelectWithScroll = (jobId: number) => {
    // Call the original onJobSelect function
    onJobSelect(jobId);
    
    // Scroll right panel to top on PC view (768px and above)
    if (window.innerWidth >= 768) {
      // 求人詳細の読み込みが完了するまで少し待ってからスクロール処理を実行
      setTimeout(() => {
        // ERBテンプレートの要素を確実に発見するためのヘルパー関数
        const findERBElement = (selector: string, maxRetries = 10, delay = 50) => {
          return new Promise<Element | null>((resolve) => {
            let retries = 0;
            const checkElement = () => {
              const element = document.querySelector(selector);
              if (element) {
                resolve(element);
              } else if (retries < maxRetries) {
                retries++;
                setTimeout(checkElement, delay);
              } else {
                resolve(null);
              }
            };
            checkElement();
          });
        };

        // 右カラム（求人詳細）のスクロール
        findERBElement('.col-md-8.col-lg-8.d-none.d-md-block').then((rightColumn) => {
          if (rightColumn) {
            rightColumn.scrollTo({ top: 0, behavior: 'smooth' });
          }
        });
      }, 300); // 求人詳細の読み込み完了を待つため少し長めに設定
    }
  };

  // Build query string for pagination URLs
  const buildQueryString = (page: number): string => {
    const params = new URLSearchParams();
    
    if (searchFilters?.keyword) params.append('keyword', searchFilters.keyword);
    searchFilters?.skill_ids?.forEach(id => params.append('skill_ids[]', id.toString()));
    searchFilters?.occupation_ids?.forEach(id => params.append('occupation_ids[]', id.toString()));
    searchFilters?.industry_ids?.forEach(id => params.append('industry_ids[]', id.toString()));
    searchFilters?.characteristic_ids?.forEach(id => params.append('characteristic_ids[]', id.toString()));
    searchFilters?.prefecture_ids?.forEach(id => params.append('prefecture_ids[]', id.toString()));
    searchFilters?.workstyle_types?.forEach((type: string) => params.append('workstyle_types[]', type));
    searchFilters?.availability_types?.forEach((type: string) => params.append('availability_types[]', type));
    if (searchFilters?.price_over) params.append('price_over', searchFilters.price_over.toString());
    if (searchFilters?.payment_site) params.append('payment_site', searchFilters.payment_site);
    if (searchFilters?.settlement_hours) params.append('settlement_hours', searchFilters.settlement_hours);
    if (searchFilters?.interview_count_max) params.append('interview_count_max', searchFilters.interview_count_max.toString());
    if (searchFilters?.published_within) params.append('published_within', searchFilters.published_within.toString());
    if (searchFilters?.is_recruiting) params.append('is_recruiting', 'true');
    searchFilters?.remote_types?.forEach(type => params.append('remote_types[]', type));
    searchFilters?.workday_types?.forEach(type => params.append('workday_types[]', type));
    if (sortBy) params.append('sort', sortBy);
    if (page > 1) params.append('page', page.toString());
    
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  };

  // 検索条件のバッジを生成する関数
  const generateFilterBadges = () => {
    if (!searchFilters || !onRemoveFilter) return null;

    const badges = [];

    // キーワード
    if (searchFilters.keyword) {
      badges.push(
        <span key="keyword" className="badge bg-primary me-1 mb-1">
          キーワード: {searchFilters.keyword}
          <button
            type="button"
            className="btn-close btn-close-white ms-1"
            style={{ fontSize: '0.6em' }}
            onClick={() => onRemoveFilter('keyword')}
          ></button>
        </span>
      );
    }

    // 価格下限
    if (searchFilters.price_over) {
      const priceLabel = `${(searchFilters.price_over / 10000).toFixed(0)}万円～`;
      badges.push(
        <span key="price_over" className="badge bg-success me-1 mb-1">
          単価: {priceLabel}
          <button
            type="button"
            className="btn-close btn-close-white ms-1"
            style={{ fontSize: '0.6em' }}
            onClick={() => onRemoveFilter('price_over')}
          ></button>
        </span>
      );
    }

    // 募集中
    if (searchFilters.is_recruiting) {
      badges.push(
        <span key="is_recruiting" className="badge me-1 mb-1" style={{ backgroundColor: '#00bb94', color: 'white' }}>
          募集中
          <button
            type="button"
            className="btn-close btn-close-white ms-1"
            style={{ fontSize: '0.6em' }}
            onClick={() => onRemoveFilter('is_recruiting')}
          ></button>
        </span>
      );
    }

    // 勤務形態（個別バッジとして表示）
    if (searchFilters.remote_types && searchFilters.remote_types.length > 0) {
      searchFilters.remote_types.forEach((remoteType: string) => {
        const remoteLabel = (() => {
          switch(remoteType) {
            case 'remote': return 'リモートOK';
            case 'full_remote': return 'フルリモート';
            case 'onsite': return '常駐';
            default: return remoteType;
          }
        })();
        badges.push(
          <span key={`remote_${remoteType}`} className="badge bg-info me-1 mb-1">
            <i className="fas fa-home me-1"></i>
            {remoteLabel}
            <button
              type="button"
              className="btn-close btn-close-white ms-1"
              style={{ fontSize: '0.6em' }}
              onClick={() => onRemoveFilter('remote_types', remoteType)}
              aria-label={`${remoteLabel}を削除`}
            ></button>
          </span>
        );
      });
    }

    // スキル（個別バッジとして表示）
    if (searchFilters.skill_ids && searchFilters.skill_ids.length > 0) {
      searchFilters.skill_ids.forEach((skillId: number) => {
        const skillName = skillNames[skillId];
        if (skillName) {
          badges.push(
            <span key={`skill_${skillId}`} className="badge bg-secondary me-1 mb-1">
              <i className="fas fa-code me-1"></i>
              {skillName}
              <button
                type="button"
                className="btn-close btn-close-white ms-1"
                style={{ fontSize: '0.6em' }}
                onClick={() => onRemoveFilter('skill_ids', skillId)}
                aria-label={`${skillName}を削除`}
              ></button>
            </span>
          );
        }
      });
    }

    // 職種（個別バッジとして表示）
    if (searchFilters.occupation_ids && searchFilters.occupation_ids.length > 0) {
      searchFilters.occupation_ids.forEach((occupationId: number) => {
        const occupationName = occupationNames[occupationId];
        if (occupationName) {
          badges.push(
            <span key={`occupation_${occupationId}`} className="badge bg-dark me-1 mb-1">
              <i className="fas fa-briefcase me-1"></i>
              {occupationName}
              <button
                type="button"
                className="btn-close btn-close-white ms-1"
                style={{ fontSize: '0.6em' }}
                onClick={() => onRemoveFilter('occupation_ids', occupationId)}
                aria-label={`${occupationName}を削除`}
              ></button>
            </span>
          );
        }
      });
    }

    // 業界（個別バッジとして表示）
    if (searchFilters.industry_ids && searchFilters.industry_ids.length > 0) {
      searchFilters.industry_ids.forEach((industryId: number) => {
        const industryName = industryNames[industryId];
        if (industryName) {
          badges.push(
            <span key={`industry_${industryId}`} className="badge bg-light text-dark me-1 mb-1">
              <i className="fas fa-industry me-1"></i>
              {industryName}
              <button
                type="button"
                className="btn-close ms-1"
                style={{ fontSize: '0.6em' }}
                onClick={() => onRemoveFilter('industry_ids', industryId)}
                aria-label={`${industryName}を削除`}
              ></button>
            </span>
          );
        }
      });
    }

    // 週日数（個別バッジとして表示）
    if (searchFilters.workday_types && searchFilters.workday_types.length > 0) {
      searchFilters.workday_types.forEach((workdayType: string) => {
        const workdayLabel = (() => {
          switch(workdayType) {
            case '1':
            case 'one_day_in_a_week':
              return '1日';
            case '2':
            case 'two_days_in_a_week':
              return '2日';
            case '3':
            case 'three_days_in_a_week':
              return '3日';
            case '4':
            case 'four_days_in_a_week':
              return '4日';
            case '5':
            case 'five_days_in_a_week':
              return '5日';
            default: 
              return workdayType;
          }
        })();
        badges.push(
          <span key={`workday_${workdayType}`} className="badge bg-purple me-1 mb-1" style={{ backgroundColor: '#6f42c1' }}>
            <i className="fas fa-calendar-week me-1"></i>
            {workdayLabel}
            <button
              type="button"
              className="btn-close btn-close-white ms-1"
              style={{ fontSize: '0.6em' }}
              onClick={() => onRemoveFilter('workday_types', workdayType)}
              aria-label={`週${workdayLabel}を削除`}
            ></button>
          </span>
        );
      });
    }

    // 特徴（個別バッジとして表示）
    if (searchFilters.characteristic_ids && searchFilters.characteristic_ids.length > 0) {
      searchFilters.characteristic_ids.forEach((characteristicId: number) => {
        const characteristicName = characteristicNames[characteristicId];
        if (characteristicName) {
          badges.push(
            <span key={`characteristic_${characteristicId}`} className="badge bg-danger me-1 mb-1">
              <i className="fas fa-star me-1"></i>
              {characteristicName}
              <button
                type="button"
                className="btn-close btn-close-white ms-1"
                style={{ fontSize: '0.6em' }}
                onClick={() => onRemoveFilter('characteristic_ids', characteristicId)}
                aria-label={`${characteristicName}を削除`}
              ></button>
            </span>
          );
        }
      });
    }

    // 都道府県（個別バッジとして表示）
    if (searchFilters.prefecture_ids && searchFilters.prefecture_ids.length > 0) {
      searchFilters.prefecture_ids.forEach((prefectureId: number) => {
        const prefectureName = prefectureNames[prefectureId];
        if (prefectureName) {
          badges.push(
            <span key={`prefecture_${prefectureId}`} className="badge bg-success me-1 mb-1">
              <i className="bi bi-geo-alt me-1"></i>
              {prefectureName}
              <button
                type="button"
                className="btn-close btn-close-white ms-1"
                style={{ fontSize: '0.6em' }}
                onClick={() => onRemoveFilter('prefecture_ids', prefectureId)}
                aria-label={`${prefectureName}を削除`}
              ></button>
            </span>
          );
        }
      });
    }

    // フリーランスエージェント（個別バッジとして表示）
    if (searchFilters.job_source_ids && searchFilters.job_source_ids.length > 0) {
      searchFilters.job_source_ids.forEach((jobSourceId: number) => {
        const jobSourceName = jobSourceNames[jobSourceId];
        if (jobSourceName) {
          badges.push(
            <span key={`job_source_${jobSourceId}`} className="badge bg-info me-1 mb-1">
              <i className="fas fa-building me-1"></i>
              {jobSourceName}
              <button
                type="button"
                className="btn-close btn-close-white ms-1"
                style={{ fontSize: '0.6em' }}
                onClick={() => onRemoveFilter('job_source_ids', jobSourceId)}
                aria-label={`${jobSourceName}を削除`}
              ></button>
            </span>
          );
        }
      });
    }

    return badges.length > 0 ? badges : null;
  };

  const sortOptions = [
    { value: 'created_at desc', label: '新着順' },
    { value: 'max_reward desc', label: '単価の高い順' },
    { value: 'max_reward asc', label: '単価の低い順' }
  ];

  return (
    <div className="d-flex flex-column job-search-sidebar" ref={sidebarRef}>
      {/* 結果ヘッダー */}
      <div className="mb-3 flex-shrink-0">
        {/* 検索条件バッジ */}
        {generateFilterBadges() && (
          <div className="mb-2">
            <div className="d-flex flex-wrap">
              {generateFilterBadges()}
            </div>
          </div>
        )}
        <div className="text-start mb-2">
          <span className="fw-bold small">
            全 {totalCount.toLocaleString()} 件中 {from}-{to}件表示
          </span>
        </div>
        <div className="text-center">
          <select 
            className="form-select form-select-sm" 
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value)}
          >
            {sortOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 求人一覧 */}
      <div className="job-listings flex-grow-1">
        {isLoading ? (
          // スケルトンローダーを表示（既存の求人数に合わせて）
          Array.from({ length: Math.max(jobListings.length, 5) }).map((_, index) => (
            <JobListingCardSkeleton key={`skeleton-${index}`} />
          ))
        ) : jobListings.length > 0 ? (
          jobListings.map(jobListing => (
            <JobListingCard
              key={jobListing.id}
              jobListing={jobListing}
              onClick={handleJobSelectWithScroll}
              isSelected={selectedJobId === jobListing.id}
            />
          ))
        ) : (
          <div className="text-center py-5">
            <i className="fas fa-search fa-3x text-muted mb-3"></i>
            <h5 className="text-muted">該当する案件が見つかりませんでした</h5>
            <p className="text-muted">検索条件を変更してお試しください</p>
          </div>
        )}
      </div>

      {/* ページネーション */}
      {totalCount > perPage && (
        <nav className="mt-4 flex-shrink-0">
          <ul className="pagination justify-content-center">
            {/* 前のページ */}
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <a 
                href={`/job_listings/search${buildQueryString(currentPage - 1)}`}
                className="page-link"
                onClick={(e) => { e.preventDefault(); handlePageChangeWithScroll(currentPage - 1); }}
              >
                前へ
              </a>
            </li>

            {/* ページ番号の動的生成 */}
            {(() => {
              const totalPages = Math.ceil(totalCount / perPage);
              const maxVisible = 5;
              const pages = [];
              
              let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
              let end = Math.min(totalPages, start + maxVisible - 1);
              
              if (end - start + 1 < maxVisible) {
                start = Math.max(1, end - maxVisible + 1);
              }

              // 最初のページと省略記号
              if (start > 1) {
                pages.push(
                  <li key={1} className="page-item">
                    <a 
                      href={`/job_listings/search${buildQueryString(1)}`}
                      className="page-link"
                      onClick={(e) => { e.preventDefault(); handlePageChangeWithScroll(1); }}
                    >
                      1
                    </a>
                  </li>
                );
                if (start > 2) {
                  pages.push(
                    <li key="ellipsis-start" className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  );
                }
              }

              // ページ番号
              for (let i = start; i <= end; i++) {
                pages.push(
                  <li key={i} className={`page-item ${i === currentPage ? 'active' : ''}`}>
                    <a 
                      href={`/job_listings/search${buildQueryString(i)}`}
                      className="page-link"
                      onClick={(e) => { e.preventDefault(); handlePageChangeWithScroll(i); }}
                    >
                      {i}
                    </a>
                  </li>
                );
              }

              // 最後のページと省略記号
              if (end < totalPages) {
                if (end < totalPages - 1) {
                  pages.push(
                    <li key="ellipsis-end" className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  );
                }
                pages.push(
                  <li key={totalPages} className="page-item">
                    <a 
                      href={`/job_listings/search${buildQueryString(totalPages)}`}
                      className="page-link"
                      onClick={(e) => { e.preventDefault(); handlePageChangeWithScroll(totalPages); }}
                    >
                      {totalPages}
                    </a>
                  </li>
                );
              }

              return pages;
            })()}
            
            {/* 次のページ */}
            <li className={`page-item ${currentPage >= Math.ceil(totalCount / perPage) ? 'disabled' : ''}`}>
              <a 
                href={`/job_listings/search${buildQueryString(currentPage + 1)}`}
                className="page-link"
                onClick={(e) => { e.preventDefault(); handlePageChangeWithScroll(currentPage + 1); }}
              >
                次へ
              </a>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

export default JobListingList;
